/**
 * @file   MatchDemandPanel.js
 * @brief  Orchestrates the demand insight display on Match tab.
 *         Uses detectTier() to determine what to show based on match count:
 *         Tiers 1-2 (3+): stats + clickable histogram + slider + insight
 *         Tier 3 (1-2): asking price + slider + insight
 *         Tier 4 (0): LowDataState notification promise
 */
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { detectTier } from "../../../helpers/matchDemandUtils";
import { calculatePropertyPriceRange, formatCompactPrice } from "../../../helpers/priceCapUtils";
import { BUY, LONG_RENT } from "../../../constants/common";
import { downloadIconTwo } from "@/images";
import DemandHistogram from "./DemandHistogram";
import SinglePriceSlider from "./SinglePriceSlider";
import QuickStatsStrip from "./QuickStatsStrip";
import InsightCallout from "./InsightCallout";
import LowDataState from "./LowDataState";
import "./MatchDemandPanel.scss";

const DOWNLOAD_REPORT_ENABLED = false;

/**
 * Extract prices from match results for histogram computation.
 * Uses average of Budget_mn/Budget_mx when both exist.
 */
const extractPrices = (adList) => {
  if (!adList || adList.length === 0) return [];
  return adList
    .map((ad) => {
      const mn = ad.Budget_mn;
      const mx = ad.Budget_mx;
      if (mn && mx) return (mn + mx) / 2;
      if (mn) return mn;
      if (mx) return mx;
      return null;
    })
    .filter((p) => p != null && p > 0);
};

const NUM_BINS = 12;

const MatchDemandPanel = ({
  selectedProperty,
  adType,
  totalCountMatch,
  adListGitchaGenie,
  isAgent,
  loading,
}) => {
  const navigate = useNavigate();
  const [sliderPrice, setSliderPrice] = useState(null);
  const [activeBinIdx, setActiveBinIdx] = useState(null);

  const matchCount = totalCountMatch || 0;
  const tier = detectTier(matchCount);

  const handleDownloadReport = () => {
    const inventoryId =
      selectedProperty?.Inv_Id ||
      selectedProperty?.inventoryId ||
      selectedProperty?.inv_id ||
      selectedProperty?.id;
    if (!inventoryId) {
      console.warn('[ActiveBuyerReport] No inventoryId on selectedProperty', selectedProperty);
      return;
    }
    navigate(`/reports/active-buyer/${inventoryId}?edit=1`);
  };

  // Get asking price from selected property
  const askingPrice = useMemo(() => {
    if (!selectedProperty) return 0;
    if (adType === BUY) return selectedProperty.AskingPrice_Sell || 0;
    if (adType === LONG_RENT) return selectedProperty.AskingPrice_Rent || 0;
    return selectedProperty.AskingPrice_Night || 0;
  }, [selectedProperty, adType]);

  // Calculate slider range from property price
  const priceRange = useMemo(
    () => calculatePropertyPriceRange(askingPrice, adType),
    [askingPrice, adType],
  );

  // Extract match prices for histogram
  const allPrices = useMemo(
    () => extractPrices(adListGitchaGenie),
    [adListGitchaGenie],
  );

  const rangeMin = priceRange?.min || 0;
  const rangeMax = priceRange?.max || 0;

  // Filter to prices relevant to this property's range so stats and
  // histogram only reflect demand near the asking price
  const prices = useMemo(() => {
    if (!rangeMin && !rangeMax) return allPrices;
    return allPrices.filter((p) => p >= rangeMin && p <= rangeMax);
  }, [allPrices, rangeMin, rangeMax]);

  // When a bin is clicked, derive sliderPrice from it; slider also updates independently
  const effectiveSliderPrice = useMemo(() => {
    if (activeBinIdx != null && rangeMax) {
      const span = rangeMax - rangeMin;
      const binWidth = span / NUM_BINS;
      return rangeMin + (activeBinIdx + 0.5) * binWidth;
    }
    return sliderPrice;
  }, [activeBinIdx, rangeMin, rangeMax, sliderPrice]);

  // Slider is exploration-only: it drives the histogram highlight and insight
  // text locally, but must NOT re-run the match search. Re-fetching would drop
  // totalCountMatch to 0 at low prices and collapse the whole panel.
  const handleSliderChange = (val) => {
    setSliderPrice(val);
    setActiveBinIdx(null);
  };

  // Histogram bin clicks only highlight the bar / move the slider locally.
  const handleBinClick = (idx) => {
    setActiveBinIdx(idx);
  };

  // Reset slider and active bin when property changes
  const propertyId = selectedProperty?.Inv_Id;
  useEffect(() => {
    setSliderPrice(null);
    setActiveBinIdx(null);
  }, [propertyId]);

  // Asking price block - reused in Tier 1 and Tier 2
  const askingPriceBlock = askingPrice > 0 && (
    <div className="match-asking-price">
      <span className="match-asking-price__label">Asking Price</span>
      <div className="match-asking-price__row">
        <span className="match-asking-price__value">{formatCompactPrice(askingPrice)}</span>
        <span className="match-asking-price__badge">ASKING</span>
      </div>
    </div>
  );

  // Match data not resolved yet - skeleton, not the tier-4 empty state
  if (loading) {
    return (
      <div className="match-demand-panel match-demand-skeleton">
        <div className="match-demand-skeleton__price shimmer" />
        <div className="match-demand-skeleton__bars shimmer" />
        <div className="match-demand-skeleton__slider shimmer" />
        <div className="match-demand-skeleton__insight shimmer" />
      </div>
    );
  }

  // Tier 4: 0 matches - notification promise
  if (tier === 4) {
    return <LowDataState />;
  }

  // Tier 3: 1-2 matches - asking price + slider + insight (no stats, no histogram)
  // Consumers see asking price only; agents get slider + insight text
  if (tier === 3) {
    return (
      <div className="match-demand-panel">
        {askingPriceBlock}
        {isAgent && rangeMax > 0 && (
          <SinglePriceSlider
            value={sliderPrice ?? askingPrice}
            onChange={handleSliderChange}
            min={rangeMin}
            max={rangeMax}
            step={priceRange?.step}
            askingPrice={askingPrice}
          />
        )}
        {isAgent && (
          <InsightCallout
            askingPrice={askingPrice}
            sliderPrice={sliderPrice}
            prices={prices}
            matchCount={matchCount}
            adType={adType}
            adList={adListGitchaGenie}
          />
        )}
        {isAgent && DOWNLOAD_REPORT_ENABLED && (
          <Button
            className="btn-grey w-100"
            data-tour="download-report-match"
            onClick={handleDownloadReport}
            style={{
              marginTop: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 40,
              padding: 0,
              letterSpacing: "unset",
            }}
          >
            Download Report
            <img
              src={downloadIconTwo}
              style={{ width: 12, height: 12, marginLeft: 8 }}
              alt=""
            />
          </Button>
        )}
      </div>
    );
  }

  // Tiers 1-2: 3+ matches - full experience with clickable histogram + slider
  // Consumers see asking price only; agents get stats, histogram, slider, insight
  return (
    <div className="match-demand-panel">
      {askingPriceBlock}
      {isAgent && (
        <QuickStatsStrip
          matchCount={matchCount}
          prices={prices}
          adType={adType}
          rangeMin={rangeMin}
          rangeMax={rangeMax}
          askingPrice={askingPrice}
        />
      )}
      {isAgent && rangeMax > 0 && (
        <>
          <DemandHistogram
            prices={prices}
            adType={adType}
            askingPrice={askingPrice}
            activeBinIdx={activeBinIdx}
            onBinClick={handleBinClick}
            rangeMin={rangeMin}
            rangeMax={rangeMax}
          />
          <SinglePriceSlider
            value={effectiveSliderPrice ?? askingPrice}
            onChange={handleSliderChange}
            min={rangeMin}
            max={rangeMax}
            step={priceRange?.step}
            askingPrice={askingPrice}
          />
        </>
      )}
      {isAgent && (
        <InsightCallout
          askingPrice={askingPrice}
          sliderPrice={effectiveSliderPrice}
          prices={prices}
          matchCount={matchCount}
          adType={adType}
          adList={adListGitchaGenie}
        />
      )}
      {isAgent && (
        <Button
          className="btn-grey w-100"
          data-tour="download-report-match"
          onClick={handleDownloadReport}
          style={{
            marginTop: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 40,
            padding: 0,
            letterSpacing: "unset",
          }}
        >
          Download Report
          <img
            src={downloadIconTwo}
            style={{ width: 12, height: 12, marginLeft: 8 }}
            alt=""
          />
        </Button>
      )}
    </div>
  );
};

export default MatchDemandPanel;
